import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import axios from '../utils/axios';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Alert } from '../components/ui/Alert';
import { Badge } from '../components/ui/Badge';
import { ModelComparisonView } from '../components/ui/ModelComparisonView';
import Layout from '../components/layout/Layout';

interface AnalysisResult {
  success?: boolean;
  project_id?: number;
  analysis_results: {
    roof?: {
      total_area_sqft: number;
      total_with_waste_sqft: number;
      planes: any[];
      waste_factor: number;
      features?: {
        ridges?: number;
        valleys?: number;
        chimneys?: number;
      };
      materials_estimate: {
        shingles_bundles: number;
        underlayment_sqft: number;
        drip_edge_lf: number;
        ridge_cap_lf: number;
      };
    };
    siding?: {
      net_siding_area_sqft: number;
      total_with_waste_sqft: number;
      walls: any[];
      waste_factor: number;
      materials_estimate: {
        siding_sqft: number;
        trim_lf: number;
        j_channel_lf: number;
        corner_posts: number;
      };
    };
    confidence_score?: number;
    live_inference_data?: any;
    baseline?: any;
    trained?: any;
  };
  remaining_credits?: number;
  report_path?: string;
}

const Analysis: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [address, setAddress] = useState('');
  const [analysisMode, setAnalysisMode] = useState<'address' | 'live_inference'>('live_inference');
  const [useAdvancedAI, setUseAdvancedAI] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState('');
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isLoadingProject, setIsLoadingProject] = useState(false);

  // Load existing project if projectId is in URL
  useEffect(() => {
    const projectId = searchParams.get('projectId');
    if (projectId) {
      loadExistingProject(projectId);
    }
  }, [searchParams]);

  const loadExistingProject = async (projectId: string) => {
    setIsLoadingProject(true);
    try {
      const response = await axios.get(`/api/project/${projectId}`);
      if (response.data) {
        setAnalysisResult(response.data);
        setAddress(response.data.address || '');
        setAnalysisMode('address'); // Existing projects are address-based
      }
    } catch (error) {
      setError('Failed to load project details');
    } finally {
      setIsLoadingProject(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (analysisMode === 'address' && !address.trim()) {
      setError('Please enter a property address');
      return;
    }

    if (analysisMode === 'address' && (!user?.credits || user.credits < 1)) {
      setError('Insufficient credits. Please purchase more credits.');
      return;
    }

    setIsAnalyzing(true);
    setError('');

    try {
      let response;
      
      if (analysisMode === 'address') {
        const formData = new FormData();
        formData.append('address', address);

        response = await axios.post('/api/analyze', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 30000
        });
      } else {
        const apiUrl = `/api/live-inference-proxy?address=${encodeURIComponent(address)}&comparison_mode=${useAdvancedAI}`;
        
        response = await axios.post(apiUrl, {}, { timeout: 30000 });
        
        if (response.data.success) {
          const liveData = response.data;
          const trainedModel = liveData.analysis_results?.trained;
          const baselineModel = liveData.analysis_results?.baseline;
          const modelData = trainedModel || baselineModel;
          
          if (modelData) {
            const measurements = modelData.measurements;
            
            // Convert satellite_image_base64 to data URL for display
            if (liveData.satellite_image_base64 && liveData.visualization) {
              liveData.visualization.satellite_image_url = `data:image/png;base64,${liveData.satellite_image_base64}`;
            }
            
            response.data = {
              success: true,
              project_id: Date.now(),
              analysis_results: {
                roof: {
                  total_area_sqft: measurements.summary?.total_roof_area_square_feet || 0,
                  total_with_waste_sqft: (measurements.summary?.total_roof_area_square_feet || 0) * 1.1,
                  planes: measurements.individual_roofs || [],
                  waste_factor: 0.1,
                  materials_estimate: {
                    shingles_bundles: Math.ceil((measurements.summary?.total_roof_area_square_feet || 0) / 33.3),
                    underlayment_sqft: measurements.summary?.total_roof_area_square_feet || 0,
                    drip_edge_lf: measurements.summary?.total_roof_perimeter_feet || 0,
                    ridge_cap_lf: (measurements.summary?.total_roof_perimeter_feet || 0) * 0.3
                  }
                },
                siding: {
                  net_siding_area_sqft: measurements.siding_estimates?.total_siding_area_square_feet || 0,
                  total_with_waste_sqft: (measurements.siding_estimates?.total_siding_area_square_feet || 0) * 1.15,
                  walls: [{height: measurements.siding_estimates?.estimated_wall_height_feet || 0}],
                  waste_factor: 0.15,
                  materials_estimate: {
                    siding_sqft: measurements.siding_estimates?.total_siding_area_square_feet || 0,
                    trim_lf: measurements.summary?.total_roof_perimeter_feet || 0,
                    j_channel_lf: (measurements.summary?.total_roof_perimeter_feet || 0) * 1.1,
                    corner_posts: 4
                  }
                },
                confidence_score: modelData.model_info?.confidence_score || 0,
                live_inference_data: liveData
              },
              remaining_credits: user?.credits
            };
          } else {
            response.data = {
              success: false,
              project_id: Date.now(),
              analysis_results: {
                roof: { total_area_sqft: 0, total_with_waste_sqft: 0, planes: [], waste_factor: 0.1, materials_estimate: { shingles_bundles: 0, underlayment_sqft: 0, drip_edge_lf: 0, ridge_cap_lf: 0 } },
                siding: { net_siding_area_sqft: 0, total_with_waste_sqft: 0, walls: [], waste_factor: 0.15, materials_estimate: { siding_sqft: 0, trim_lf: 0, j_channel_lf: 0, corner_posts: 0 } },
                confidence_score: 0
              },
              remaining_credits: user?.credits
            };
          }
        }
      }
      
      setAnalysisResult(response.data);
    } catch (err: any) {
      // Log error for debugging in development only
      if (process.env.NODE_ENV === 'development') {
        console.error('Analysis Error:', err);
      }
      
      if (err.response?.status === 500 && err.response?.data?.detail?.includes('AI')) {
        setError(`AI Analysis Failed: ${err.response.data.detail}. This address may not have clear satellite imagery. Please try a different address.`);
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Analysis failed. Please try again with a different address.');
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!analysisResult) return;

    setIsGeneratingReport(true);

    try {
      // Get project ID from analysisResult or URL params (for database projects)
      const projectId = analysisResult.project_id || searchParams.get('projectId');
      
      if (!projectId) {
        alert('Project ID not found. Please try analyzing again.');
        return;
      }

      // For free reports during beta, don't require payment
      // Just generate the report directly
      const response = await axios.post(`/api/generate-report/${projectId}`, {
        address: address,
        analysis_results: analysisResult.analysis_results
      }, {
        headers: { 'Content-Type': 'application/json' },
      });
      
      if (response.data.report_path) {
        // Use authenticated axios download instead of window.open
        const downloadResponse = await axios.get(`/api/download-report/${projectId}`, {
          responseType: 'blob',
        });
        
        const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `mavscope_report_${projectId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        alert('Report downloaded successfully!');
      }
    } catch (err: any) {
      console.error('Failed to generate report:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to generate report. Please try again.';
      alert(errorMsg);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleNewAnalysis = () => {
    setAnalysisResult(null);
    setAddress('');
    setError('');
  };

  return (
    <Layout>
      <div className="analysis-page">
        <header className="analysis-header">
          <div className="analysis-header-content">
            <button className="back-button" onClick={() => navigate('/dashboard')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back to Dashboard
            </button>
            
            <div className="credits-display">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v6l4 2" />
              </svg>
              <span>{user?.credits || 0} Credits</span>
            </div>
          </div>
        </header>

      <main className="analysis-content">
        {!analysisResult ? (
          <div className="analysis-form-container">
            <div className="analysis-intro">
              <h1 className="analysis-title">Property Analysis</h1>
              <p className="analysis-description">
                Get AI-powered roof and siding measurements from satellite imagery
              </p>
            </div>

            <Card>
              <CardBody>
                <div className="analysis-tabs">
                  <button 
                    type="button"
                    className={`analysis-tab ${analysisMode === 'address' ? 'active' : ''}`}
                    onClick={() => setAnalysisMode('address')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 6v6l4 2" />
                    </svg>
                    Standard Analysis
                  </button>
                  <button 
                    type="button"
                    className={`analysis-tab ${analysisMode === 'live_inference' ? 'active' : ''}`}
                    onClick={() => setAnalysisMode('live_inference')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                    </svg>
                    Live Preview (Beta)
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="analysis-form">
                  <div className="form-group">
                    <Input
                      label="Property Address"
                      type="text"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Enter full address (e.g., 123 Main St, City, State ZIP)"
                      required
                    />
                    
                    {analysisMode === 'live_inference' && (
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={useAdvancedAI}
                          onChange={(e) => setUseAdvancedAI(e.target.checked)}
                          className="checkbox-input"
                        />
                        Use Advanced AI Comparison (Trained vs Baseline)
                      </label>
                    )}
                  </div>

                  {error && <Alert variant="error">{error}</Alert>}

                  {analysisMode === 'address' ? (
                    <div className="cost-summary">
                      <div className="cost-row">
                        <span>Analysis Cost</span>
                        <span>1 Credit</span>
                      </div>
                      <div className="cost-row">
                        <span>Your Balance</span>
                        <span>{user?.credits || 0} Credits</span>
                      </div>
                      <div style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)' }}>
                        Saved to your account with full report generation
                      </div>
                    </div>
                  ) : (
                    <div className="info-banner">
                      <p className="info-title">🚀 Live Preview Mode</p>
                      <p className="info-text">
                        Real-time AI analysis with satellite imagery visualization. Compare baseline vs advanced AI models. Free during beta testing.
                      </p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    variant="primary"
                    size="lg"
                    fullWidth
                    isLoading={isAnalyzing}
                    disabled={analysisMode === 'address' && (!user?.credits || user.credits < 1)}
                  >
                    {isAnalyzing ? 'Analyzing Property...' : 'Analyze Property'}
                  </Button>
                </form>

                <div className="features-section">
                  <h3 className="features-title">What You'll Get</h3>
                  <div className="features-list">
                    {[
                      'Detailed roof measurements and area calculations',
                      'Siding area with window/door deductions',
                      'Material estimates with waste factors',
                      'Professional PDF report',
                      'AI confidence score'
                    ].map((item, i) => (
                      <div key={i} className="feature-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent)" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        ) : (
          <div className="results-container">
            <Card className="result-header-card">
              <CardBody>
                <div className="result-header">
                  <div>
                    <h2 className="result-title">Analysis Complete</h2>
                    <p className="result-address">{address}</p>
                  </div>
                  <Badge variant="success" className="confidence-badge">
                    {((analysisResult.analysis_results.confidence_score || 0) * 100).toFixed(1)}% Confidence
                  </Badge>
                </div>
              </CardBody>
            </Card>

            {/* Visual Results Section */}
            {analysisResult.analysis_results.live_inference_data && (
              <ModelComparisonView
                baselineModel={analysisResult.analysis_results.live_inference_data.analysis_results?.baseline ? {
                  modelType: 'Standard AI Model',
                  confidence: analysisResult.analysis_results.live_inference_data.analysis_results.baseline.model_info?.confidence_score || 0,
                  roofArea: analysisResult.analysis_results.live_inference_data.analysis_results.baseline.measurements?.summary?.total_roof_area_square_feet || 0,
                  sidingArea: analysisResult.analysis_results.live_inference_data.analysis_results.baseline.measurements?.siding_estimates?.total_siding_area_square_feet || 0
                } : undefined}
                trainedModel={analysisResult.analysis_results.live_inference_data.analysis_results?.trained ? {
                  modelType: 'MavScope Pro AI (Trained on 50K+ Properties)',
                  confidence: analysisResult.analysis_results.live_inference_data.analysis_results.trained.model_info?.confidence_score || 0,
                  roofArea: analysisResult.analysis_results.live_inference_data.analysis_results.trained.measurements?.summary?.total_roof_area_square_feet || 0,
                  sidingArea: analysisResult.analysis_results.live_inference_data.analysis_results.trained.measurements?.siding_estimates?.total_siding_area_square_feet || 0
                } : undefined}
                satelliteImageUrl={analysisResult.analysis_results.live_inference_data.visualization?.satellite_image_url}
              />
            )}

            <div className="metrics-grid">
                <Card>
                  <CardBody>
                    <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      </svg>
                      Roof Analysis
                    </h3>
                    {[
                      { label: 'Total Area', value: `${analysisResult.analysis_results.roof?.total_area_sqft?.toLocaleString() || 0} sq ft` },
                      { label: 'With Waste', value: `${analysisResult.analysis_results.roof?.total_with_waste_sqft?.toLocaleString() || 0} sq ft` },
                      { label: 'Planes', value: analysisResult.analysis_results.roof?.planes?.length || 0 },
                      { label: 'Waste Factor', value: `${((analysisResult.analysis_results.roof?.waste_factor || 0) * 100).toFixed(0)}%` }
                    ].map((item, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--spacing-2) 0', borderBottom: i < 3 ? '1px solid var(--color-gray-100)' : 'none', fontSize: 'var(--text-sm)' }}>
                        <span style={{ color: 'var(--color-gray-500)' }}>{item.label}</span>
                        <span style={{ fontWeight: 'var(--font-medium)' }}>{item.value}</span>
                      </div>
                    ))}
                  </CardBody>
                </Card>

                <Card>
                  <CardBody>
                    <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', marginBottom: 'var(--spacing-4)', display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <line x1="3" y1="9" x2="21" y2="9" />
                        <line x1="9" y1="21" x2="9" y2="9" />
                      </svg>
                      Siding Analysis
                    </h3>
                    {[
                      { label: 'Net Area', value: `${analysisResult.analysis_results.siding?.net_siding_area_sqft?.toLocaleString() || 0} sq ft` },
                      { label: 'With Waste', value: `${analysisResult.analysis_results.siding?.total_with_waste_sqft?.toLocaleString() || 0} sq ft` },
                      { label: 'Walls', value: analysisResult.analysis_results.siding?.walls?.length || 0 },
                      { label: 'Waste Factor', value: `${((analysisResult.analysis_results.siding?.waste_factor || 0) * 100).toFixed(0)}%` }
                    ].map((item, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: 'var(--spacing-2) 0', borderBottom: i < 3 ? '1px solid var(--color-gray-100)' : 'none', fontSize: 'var(--text-sm)' }}>
                        <span style={{ color: 'var(--color-gray-500)' }}>{item.label}</span>
                        <span style={{ fontWeight: 'var(--font-medium)' }}>{item.value}</span>
                      </div>
                    ))}
                  </CardBody>
                </Card>
            </div>

            <Card className="materials-card">
              <CardBody>
                <h3 className="materials-title">Material Estimates</h3>
                <div className="materials-grid">
                    <div>
                      <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)', marginBottom: 'var(--spacing-3)', paddingBottom: 'var(--spacing-2)', borderBottom: '2px solid var(--color-primary)' }}>
                        Roofing Materials
                      </h4>
                      {[
                        { label: 'Shingles', value: `${analysisResult.analysis_results.roof?.materials_estimate?.shingles_bundles || 0} bundles` },
                        { label: 'Underlayment', value: `${analysisResult.analysis_results.roof?.materials_estimate?.underlayment_sqft?.toLocaleString() || 0} sq ft` },
                        { label: 'Drip Edge', value: `${analysisResult.analysis_results.roof?.materials_estimate?.drip_edge_lf?.toLocaleString() || 0} ft` },
                        { label: 'Ridge Cap', value: `${analysisResult.analysis_results.roof?.materials_estimate?.ridge_cap_lf?.toLocaleString() || 0} ft` }
                      ].map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', padding: 'var(--spacing-2) 0', borderBottom: '1px solid var(--color-gray-100)' }}>
                          <span style={{ color: 'var(--color-gray-600)' }}>{item.label}</span>
                          <span style={{ fontWeight: 'var(--font-medium)' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--color-gray-700)', marginBottom: 'var(--spacing-3)', paddingBottom: 'var(--spacing-2)', borderBottom: '2px solid var(--color-primary)' }}>
                        Siding Materials
                      </h4>
                      {[
                        { label: 'Siding', value: `${analysisResult.analysis_results.siding?.materials_estimate?.siding_sqft?.toLocaleString() || 0} sq ft` },
                        { label: 'Trim', value: `${analysisResult.analysis_results.siding?.materials_estimate?.trim_lf?.toLocaleString() || 0} ft` },
                        { label: 'J-Channel', value: `${analysisResult.analysis_results.siding?.materials_estimate?.j_channel_lf?.toLocaleString() || 0} ft` },
                        { label: 'Corner Posts', value: analysisResult.analysis_results.siding?.materials_estimate?.corner_posts || 0 }
                      ].map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', padding: 'var(--spacing-2) 0', borderBottom: '1px solid var(--color-gray-100)' }}>
                          <span style={{ color: 'var(--color-gray-600)' }}>{item.label}</span>
                          <span style={{ fontWeight: 'var(--font-medium)' }}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardBody>
            </Card>

            <div className="actions-container">
                <Button 
                  variant="primary"
                  size="lg"
                  isLoading={isGeneratingReport}
                  onClick={handleGenerateReport}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  Generate PDF Report
                </Button>
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={handleNewAnalysis}
                  disabled={!user?.credits || user.credits < 1}
                >
                  New Analysis ({user?.credits || 0} credits)
                </Button>
            </div>

            <div className="credits-remaining">
              Credits Remaining: <strong>{analysisResult.remaining_credits}</strong>
            </div>
          </div>
        )}
      </main>
    </div>
    </Layout>
  );
};

export default Analysis;
